import React, { useMemo, useState } from 'react'
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js'
import { Doughnut } from 'react-chartjs-2'
import { Box, Paper, Typography, CircularProgress } from '@mui/material'
import CloudOffIcon from '@mui/icons-material/CloudOff'

ChartJS.register(ArcElement, Tooltip, Legend)

const PercentageGauge = ({
  percent,
  color,
  title,
  target,
  hideNumber,
  loading,
  error,
  customNumber,
  reverse,
  maxHeight,
}) => {
  const colors = (c) => {
    switch (c) {
      case 'success':
        return {
          main: '#059669',
          light: 'rgba(5, 150, 105, 0.2)',
          background:
            'linear-gradient(135deg, rgba(5, 150, 105, 0.14), rgba(5, 150, 105, 0.03))',
        }
      case 'warning':
        return {
          main: '#d97706',
          light: 'rgba(237, 108, 2, 0.2)',
          background:
            'linear-gradient(135deg, rgba(245, 158, 11, 0.12), rgba(245, 158, 11, 0.02))',
        }
      case 'error':
        return {
          main: '#e11d48',
          light: 'rgba(244, 63, 94, 0.2)',
          background:
            'linear-gradient(135deg, rgba(244, 63, 94, 0.12), rgba(244, 63, 94, 0.02))',
        }
      case 'neutral':
      default:
        return {
          main: '#2563eb',
          light: 'rgba(59, 130, 246, 0.2)',
        }
    }
  }

  const numericPercent = Number(percent)
  const numericTarget = Number(target)

  const data = {
    datasets: [
      {
        data: [percent, 100 - percent],
        backgroundColor: [colors(color).main, colors(color).light],
        circumference: 180,
        rotation: 270,
        cutout: '60%',
      },
    ],
  }

  const anGaugeText = {
    id: 'anGaugeText',
    beforeDraw: (chart, args, pluginOptions) => {
      const {
        ctx,
        data,
        chartArea: { top, bottom, left, right, width, height },
      } = chart
      const xCenter = chart.getDatasetMeta(0).data[0].x
      const yCenter = chart.getDatasetMeta(0).data[0].y - 6
      ctx.restore()
      ctx.fillStyle = colors(color).main
      // ctx.font = 'bold 1rem sans-serif'
      ctx.textAlign = 'center'
      ctx.textBaseLine = 'bottom'
      ctx.fillText(
        customNumber
          ? customNumber
          : isNaN(percent)
            ? 'N/A'
            : `${Number(data.datasets[0].data[0]).toFixed(0)}%`,
        xCenter,
        yCenter,
      )
      ctx.save()
    },
  }

  const options = {
    maintainAspectRatio: false,
    borderJoinStyle: 'round',
    borderRadius: 3,
    borderWidth: 0.5,
    plugins: {
      tooltip: {
        enabled: false,
      },
    },
  }

  return (
    <Box
      sx={{
        flex: 1,
        // p: 2,
        borderRadius: 3,
        // background: `linear-gradient(135deg, ${colors.light}, transparent)`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      {loading || error ? (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          padding={2}
        >
          {loading ? (
            <CircularProgress sx={{ color: colors(color).main }} />
          ) : (
            <CloudOffIcon sx={{ color: colors(color).main }} fontSize="large" />
          )}
        </Box>
      ) : (
        <Doughnut
          data={data}
          style={{ maxHeight: maxHeight ?? '6rem' }}
          plugins={hideNumber ? [] : [anGaugeText]}
          options={options}
        />
      )}
      <Typography
        variant="caption"
        sx={{
          mt: 1,
          fontWeight: 600,
          color: 'text.secondary',
          textAlign: 'center',
        }}
      >
        {title}
      </Typography>
    </Box>
  )
}

export default PercentageGauge
